import React, { useState } from 'react';
import { AGENTS } from '../agents.js';

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

function fmtDate(d) {
  if (!d) return '';
  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return String(d).toUpperCase();
  return `${parseInt(m[3], 10)} ${MONTHS[parseInt(m[2], 10) - 1]}`;
}

function itemKey(item, i) {
  return item._id || `${item.date}|${item.from}|${item.to}|${(item.body || '').slice(0, 24)}|${i}`;
}

// A2aGroup — renders a (date, from→to) bundle of a2a turns. Collapsed by default.
// Props:
//   from, to, date, items: [{ _id?, date, from, to, body, dismissed?, reply? }]
//   onDismiss(item) -> Promise         producer dismissed this intercept
//   onReply(item, replyBody) -> Promise  producer replied (auto-dismisses)
//   scope: 'thread' | 'room'           visual variant
export default function A2aGroup({ from, to, date, items, onDismiss, onReply, scope = 'thread' }) {
  const [expanded, setExpanded] = useState(false);
  const fromAgent = AGENTS[from] || { glyph: '???', mark: 'var(--ink-3)', name: from };
  const toAgent   = AGENTS[to]   || { glyph: '???', mark: 'var(--ink-3)', name: to };

  // Hide fully-dismissed-without-reply items entirely.
  const visible = items.filter(it => !(it.dismissed && !it.reply));
  if (visible.length === 0) return null;

  return (
    <section className={`a2a-group scope-${scope} ${expanded ? 'is-open' : ''}`}>
      <button
        type="button"
        className="a2a-group-row"
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
      >
        <span className="a2a-group-pair">
          <span className="a2a-glyph" style={{ background: fromAgent.mark }}>{fromAgent.glyph}</span>
          <span className="a2a-arrow" aria-hidden>→</span>
          <span className="a2a-glyph" style={{ background: toAgent.mark }}>{toAgent.glyph}</span>
        </span>
        <span className="a2a-group-meta">
          <span className="count">{visible.length} {visible.length === 1 ? 'INTERCEPT' : 'INTERCEPTS'}</span>
          <span className="dot" aria-hidden>·</span>
          <span className="date">{fmtDate(date)}</span>
        </span>
        <span className="a2a-toggle" aria-hidden>+</span>
      </button>

      {expanded && (
        <div className="a2a-group-body" role="region">
          {visible.map((item, i) => (
            <A2aItem
              key={itemKey(item, i)}
              item={item}
              fromAgent={fromAgent}
              toAgent={toAgent}
              onDismiss={onDismiss}
              onReply={onReply}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function A2aItem({ item, fromAgent, toAgent, onDismiss, onReply }) {
  const [mode, setMode] = useState('rest'); // 'rest' | 'replying' | 'fading'
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      await onReply(item, body);
      setDraft('');
      setMode('rest');
    } catch (err) {
      alert(err.message || 'Could not send reply.');
    } finally {
      setBusy(false);
    }
  }

  async function dismiss() {
    if (busy) return;
    setMode('fading');
    setBusy(true);
    try {
      await onDismiss(item);
    } catch (err) {
      setMode('rest');
      alert(err.message || 'Could not dismiss.');
    } finally {
      setBusy(false);
    }
  }

  if (item.reply) {
    return (
      <article className="a2a-item is-replied">
        <div className="a2a-item-body">"{item.body}"</div>
        <div className="a2a-reply">
          <div className="a2a-reply-meta">YOU NOTED · {fmtDate(item.reply.date)}</div>
          <div className="a2a-reply-body">{item.reply.body}</div>
        </div>
      </article>
    );
  }

  return (
    <article className={`a2a-item mode-${mode}`}>
      <div className="a2a-item-body">"{item.body}"</div>

      {mode === 'rest' && (
        <div className="a2a-item-actions">
          <button type="button" className="a2a-act a2a-act-primary" onClick={() => setMode('replying')}>Reply</button>
          <button type="button" className="a2a-act a2a-dismiss" onClick={dismiss} disabled={busy}>Dismiss</button>
        </div>
      )}

      {mode === 'replying' && (
        <form className="a2a-reply-form" onSubmit={submit}>
          <textarea
            autoFocus
            rows={3}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder={`Note for ${fromAgent.name} & ${toAgent.name}. Both will see it next time they speak.`}
            disabled={busy}
          />
          <div className="a2a-reply-actions">
            <button type="submit" className="a2a-act a2a-act-primary" disabled={!draft.trim() || busy}>
              {busy ? 'Sending…' : 'Send & dismiss'}
            </button>
            <button type="button" className="a2a-act" onClick={() => { setDraft(''); setMode('rest'); }} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </article>
  );
}
